import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const PxToRemTool = () => {
  const [px, setPx] = useState('16');
  const [rem, setRem] = useState('1');
  const [base, setBase] = useState('16');

  const pxToRem = (pxVal, baseVal) => {
    return (parseFloat(pxVal) / parseFloat(baseVal)).toFixed(4).replace(/\.?0+$/, '');
  };

  const remToPx = (remVal, baseVal) => {
    return (parseFloat(remVal) * parseFloat(baseVal)).toFixed(2).replace(/\.?0+$/, '');
  };

  const handlePxChange = (value) => {
    setPx(value);
    if (value && !isNaN(value)) {
      setRem(pxToRem(value, base));
    }
  };

  const handleRemChange = (value) => {
    setRem(value);
    if (value && !isNaN(value)) {
      setPx(remToPx(value, base));
    }
  };

  const handleBaseChange = (value) => {
    setBase(value);
    if (px && !isNaN(px)) {
      setRem(pxToRem(px, value));
    }
  };

  const copyPx = () => {
    navigator.clipboard.writeText(`${px}px`);
    toast.success('PX value copied!');
  };

  const copyRem = () => {
    navigator.clipboard.writeText(`${rem}rem`);
    toast.success('REM value copied!');
  };

  // Common conversions
  const commonPx = [8, 12, 14, 16, 18, 20, 24, 32, 48, 64];

  return (
    <div className="space-y-6" data-testid="px-rem-tool">
      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <label className="text-sm text-muted-foreground mb-2 block">Base Font Size</label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={base}
            onChange={(e) => handleBaseChange(e.target.value)}
            className="w-24 font-mono"
          />
          <span className="text-sm text-muted-foreground">px (browser default is 16px)</span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 items-center">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Pixels (px)</label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={px}
              onChange={(e) => handlePxChange(e.target.value)}
              className="font-mono"
              placeholder="16"
            />
            <Button onClick={copyPx} variant="outline" size="icon">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowRightLeft className="w-6 h-6 text-muted-foreground" />
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">REM</label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={rem}
              onChange={(e) => handleRemChange(e.target.value)}
              className="font-mono"
              placeholder="1"
              step="0.0625"
            />
            <Button onClick={copyRem} variant="outline" size="icon">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-3">Common Conversions (base: {base}px)</h3>
        <div className="grid grid-cols-5 gap-2 text-sm">
          {commonPx.map(p => (
            <button
              key={p}
              onClick={() => handlePxChange(p.toString())}
              className="p-2 rounded bg-muted hover:bg-primary/10 transition-colors"
            >
              <span className="block font-mono">{p}px</span>
              <span className="block text-xs text-muted-foreground">{pxToRem(p, base)}rem</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => { setPx('16'); setRem('1'); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default PxToRemTool;
